// energySwordEffect.ts
import { Particle } from '../ParticleLayer'

const CONFIG = {
    coreColor: "#cca30f",
    lightningColor: "#009dff",
    speed: 4.5,
    trailLength: 1, // Actually we'll make this slightly longer for the trail effect
    lightningDensity: 2,
    bloom: 20,
    spritePath: '/assets/effects/energy_sword/EnergySword.svg'
}

const spriteCache: Record<string, HTMLImageElement> = {}
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image()
        img.src = src
        spriteCache[src] = img
    }
    return spriteCache[src]
}
getSprite(CONFIG.spritePath) // Preload

// Recursive Lightning Generator
const drawLightning = (
    ctx: CanvasRenderingContext2D,
    x1: number, y1: number,
    x2: number, y2: number,
    displace: number
) => {
    if (displace < 2) {
        ctx.lineTo(x2, y2);
        return;
    }
    const midXReal = (x1 + x2) / 2 + (Math.random() - 0.5) * displace;
    const midYReal = (y1 + y2) / 2 + (Math.random() - 0.5) * displace;
    drawLightning(ctx, x1, y1, midXReal, midYReal, displace / 2);
    drawLightning(ctx, midXReal, midYReal, x2, y2, displace / 2);
};

export type EnergySwordData = {
    state: 'slice' | 'fade'
    progress: number // Goes from 0 to 4 automatically
    waypoints: { x: number, y: number }[]
    currX: number
    currY: number
    currAngle: number
    trail: { x: number; y: number; age: number }[]
}

export type EnergySwordEffect = {
    kind: 'energySword'
    t: number
    x: number // General target area
    y: number
    isOpponent: boolean
    data: EnergySwordData
}

// Generate zigzag waypoints around a center point
const generateWaypoints = (cx: number, cy: number, numPoints: number) => {
    const points = []
    // Start slightly off-center
    points.push({ x: cx + (Math.random() - 0.5) * 300, y: cy + (Math.random() - 0.5) * 300 })
    for (let i = 0; i < numPoints; i++) {
        // Generate a point that crosses the center roughly
        const prev = points[points.length - 1]
        const dirX = cx > prev.x ? 1 : -1
        const dirY = cy > prev.y ? 1 : -1
        points.push({
            x: prev.x + dirX * (200 + Math.random() * 300) + (Math.random() - 0.5) * 100,
            y: prev.y + dirY * (200 + Math.random() * 300) + (Math.random() - 0.5) * 100
        })
    }
    return points
}

export function createEnergySwordEffect(x: number, y: number, isOpponent: boolean = false): EnergySwordEffect {
    const cx = window.innerWidth / 2
    const cy = window.innerHeight / 2

    // We want 4 quick slices crossing the center
    const waypoints = generateWaypoints(cx, cy, 4)

    return {
        kind: 'energySword',
        t: 0,
        x: cx,
        y: cy,
        isOpponent,
        data: {
            state: 'slice',
            progress: 0,
            waypoints,
            currX: waypoints[0].x,
            currY: waypoints[0].y,
            currAngle: 0,
            trail: []
        }
    }
}

export function renderEnergySwordEffect(
    effect: EnergySwordEffect,
    ctx: CanvasRenderingContext2D
): boolean {
    const data = effect.data

    // Physics & State
    // We want exactly 4 bounces matching the speed of the red attack card (0.04 per frame)
    if (data.state === 'slice') {
        const speedMultiplier = 0.04
        data.progress += speedMultiplier

        if (data.progress >= data.waypoints.length - 1) {
            data.state = 'fade'
            data.progress = 0 // reuse progress for fade
        } else {
            // Find current segment
            const index = Math.floor(data.progress)
            const t = data.progress - index // percentage of current segment

            const startX = data.waypoints[index].x
            const startY = data.waypoints[index].y
            const targetX = data.waypoints[index + 1].x
            const targetY = data.waypoints[index + 1].y

            // Linear fast blitz is best for "speed force" feeling
            data.currX = startX + (targetX - startX) * t
            data.currY = startY + (targetY - startY) * t
            // Store angle for SVG rotation
            data.currAngle = Math.atan2(targetY - startY, targetX - startX)
        }
    } else if (data.state === 'fade') {
        data.progress += 0.015
        if (data.progress >= 1 && data.trail.length === 0) {
            return false // Effect complete
        }
    }

    // Add trail points
    if (data.state === 'slice') {
        data.trail.push({ x: data.currX, y: data.currY, age: 1.0 })
    }

    // Update trail ages
    // The visual trail length depends on decaying speed
    const decayRate = 0.025
    data.trail.forEach(p => p.age -= decayRate)
    data.trail = data.trail.filter(p => p.age > 0)

    ctx.save()

    // Render Trail Glow using additive blending
    ctx.globalCompositeOperation = 'lighter'

    // Render Fading Trail Segments
    for (let i = 1; i < data.trail.length; i++) {
        const p = data.trail[i]
        const prev = data.trail[i - 1]

        ctx.beginPath()
        ctx.moveTo(prev.x, prev.y)
        ctx.lineTo(p.x, p.y)

        ctx.lineCap = 'round'
        ctx.lineWidth = (10 + CONFIG.bloom) * p.age
        ctx.strokeStyle = CONFIG.coreColor
        ctx.shadowBlur = (CONFIG.bloom * 2) * p.age
        ctx.shadowColor = CONFIG.coreColor

        const fadeMultiplier = data.state === 'fade' ? Math.max(0, 1 - data.progress) : 1
        ctx.globalAlpha = p.age * fadeMultiplier
        ctx.stroke()
        ctx.shadowBlur = 0
    }

    // Draw final segment to current point if slicing
    if (data.state === 'slice') {
        const last = data.trail[data.trail.length - 1]
        ctx.beginPath()
        ctx.moveTo(last.x, last.y)
        ctx.lineTo(data.currX, data.currY)
        ctx.lineWidth = 15 + CONFIG.bloom
        ctx.strokeStyle = CONFIG.coreColor
        ctx.shadowBlur = CONFIG.bloom * 2
        ctx.shadowColor = CONFIG.coreColor
        ctx.globalAlpha = 1
        ctx.stroke()
        ctx.shadowBlur = 0
    }

    // Draw Lightning across trail
    if (data.state === 'slice' && Math.random() < CONFIG.lightningDensity * 0.4 && data.trail.length > 5) {
        const idx1 = Math.floor(Math.random() * (data.trail.length - 5))
        const idx2 = Math.min(data.trail.length - 1, idx1 + 2 + Math.floor(Math.random() * 10))

        ctx.beginPath()
        ctx.moveTo(data.trail[idx1].x, data.trail[idx1].y)
        drawLightning(ctx, data.trail[idx1].x, data.trail[idx1].y, data.trail[idx2].x, data.trail[idx2].y, 60)

        ctx.lineWidth = 3
        ctx.strokeStyle = CONFIG.lightningColor
        ctx.shadowColor = CONFIG.lightningColor
        ctx.shadowBlur = 20
        ctx.globalAlpha = 1
        ctx.stroke()
        ctx.shadowBlur = 0
    }

    // Draw Core dot at the head
    if (data.state === 'slice') {
        ctx.fillStyle = '#fff'
        ctx.shadowColor = '#fff'
        ctx.shadowBlur = 20
        ctx.beginPath()
        ctx.arc(data.currX, data.currY, 10, 0, Math.PI * 2)
        ctx.fill()
        ctx.shadowBlur = 0
    }

    // Switch to normal composite for sprite
    ctx.globalCompositeOperation = 'source-over'
    // Ensure overall opacity drops nicely in fade
    ctx.globalAlpha = data.state === 'fade' ? Math.max(0, 1 - data.progress) : 1

    // Finally, Draw Sword SVG at head
    if (data.state === 'slice' || data.state === 'fade') {
        const img = getSprite(CONFIG.spritePath)
        // Only draw if image is valid (naturalWidth>0 avoids SVG errors)
        if (img.complete && img.naturalWidth > 0) {
            ctx.save()
            // In fade state, use last point. In slice state, use currX
            ctx.translate(data.currX, data.currY)

            // Make the SVG point strictly to the movement direction.
            // Based on EnergySword.svg, the sword leans to the top-left (-40 deg).
            // A standard rotation to match velocity is usually angle + Math.PI/2 for upward pointing blades.
            // Since this is custom, we'll align it visually (+ Math.PI/4 typical offset).
            let visualAngle = data.currAngle + Math.PI / 4

            ctx.rotate(visualAngle)

            // Downscale the giant 1000x1200 SVG to fit screen
            // Reference speedforce run implies tiny trails, but the weapon should be visible
            const scale = 0.25
            const w = img.naturalWidth * scale
            const h = img.naturalHeight * scale

            ctx.shadowColor = 'rgba(0, 0, 0, 0.8)'
            ctx.shadowBlur = 15
            ctx.shadowOffsetY = 10

            // The trail is at (0,0) (because of ctx.translate(data.currX, data.currY)).
            // We want the trail to emit from the TIP of the sword, not the center.
            // Since the sword points "up" (visually), shifting the image down (-h) means the top tip is near (0,0).
            ctx.drawImage(img, -w / 2, -h * 0.9, w, h)
            ctx.restore()
        }
    }

    ctx.restore()
    return true
}
